import React, { useState } from 'react';
import { Box, Typography, Stack, TextField, Button, Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import { Add, PersonOutline } from '@mui/icons-material';
import { GraphUser } from '../../../../../utils/MsGraphApiCall';
import { useGraphUserSearch } from '../../../hooks/useGraphUserSearch';
import { useTeamMemberManagement } from '../../../hooks/useTeamMemberManagement';

/**
 * AddTeamMember - Component for Microsoft Graph user lookup and team member addition
 */
export const AddTeamMember: React.FC = () => {
  const [selectedPerson, setSelectedPerson] = useState<GraphUser | null>(null);
  
  // Custom hooks for business logic
  const {
    people,
    loadingPeople,
    searchInputValue,
    setSearchInputValue,
    resetSearch
  } = useGraphUserSearch();

  const {
    handleAddMember: addMember
  } = useTeamMemberManagement();

  const handleAddMember = () => {
    if (!selectedPerson) {
      return;
    }

    // Always use "MEMBER" role by default
    addMember(selectedPerson, 'MEMBER');

    // Reset form
    setSelectedPerson(null);
    resetSearch();
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
        Add New Member
      </Typography>
      
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={people}
              loading={loadingPeople}
              value={selectedPerson}
              onChange={(_, newValue) => setSelectedPerson(newValue)}
              inputValue={searchInputValue}
              onInputChange={(_, newInputValue) => setSearchInputValue(newInputValue)}
              getOptionLabel={(option) => option.displayName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search for team member *"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: '#64748b', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingPeople ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.mail}
                    </Typography>
                    {option.jobTitle && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {option.jobTitle} {option.department && `• ${option.department}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              noOptionsText={searchInputValue.length < 2 ? "Type at least 2 characters to search" : "No people found"}
            />
          </Box>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={handleAddMember}
            disabled={!selectedPerson}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Member
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AddTeamMember;